/**
 * Web stub for React Native's Alert API
 * Maps React Native Alert.alert() calls to browser alert()
 */

export interface AlertButton {
  text?: string;
  onPress?: () => void | Promise<void>;
  style?: 'default' | 'cancel' | 'destructive';
}

export interface AlertOptions {
  cancelable?: boolean;
  onDismiss?: () => void;
}

/**
 * Web implementation of React Native Alert.alert
 * Uses browser's native alert or confirm dialogs
 */
const alert = (
  title: string,
  message?: string,
  buttons?: AlertButton[],
  options?: AlertOptions
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const fullMessage = message ? `${title}\n\n${message}` : title;
  const processedButtons = buttons || [{ text: 'OK' }];

  // If there's only one button or no buttons, use alert()
  if (processedButtons.length <= 1) {
    window.alert(fullMessage);
    const button = processedButtons[0];
    if (button?.onPress) {
      Promise.resolve(button.onPress());
    }
    return;
  }

  // If there are multiple buttons, use confirm() or create custom UI
  if (processedButtons.length === 2) {
    // Check if one is cancel and one is confirm
    const cancelBtn = processedButtons.find((b) => b.style === 'cancel');
    const actionBtn = processedButtons.find((b) => b.style !== 'cancel');

    if (cancelBtn && actionBtn) {
      const confirmed = window.confirm(fullMessage);
      if (confirmed) {
        if (actionBtn.onPress) {
          Promise.resolve(actionBtn.onPress());
        }
      } else {
        if (cancelBtn.onPress) {
          Promise.resolve(cancelBtn.onPress());
        }
      }
      return;
    }
  }

  // For complex cases with more than 2 buttons, fall back to alert
  window.alert(fullMessage);
  if (processedButtons[0]?.onPress) {
    Promise.resolve(processedButtons[0].onPress());
  }
};

/**
 * Web Alert module that mimics React Native's Alert
 */
const Alert = {
  alert,
};

export default Alert;
